import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SIDE_PANEL_SLOT_ID } from 'features/dashboard/chatSessions';

/**
 * A panel beside the page, not over it — the box Ask occupies.
 *
 * A drawer that covers what it is about makes you close it to look again.
 * This renders into the slot the app keeps to the right of the whole content
 * column, so the page shrinks and both stay readable: it reads as a second
 * tab rather than a lid. Same grey strip above it as above the page, so the
 * two start and end on the same line.
 *
 * Below `OVERLAY_BELOW` there is not room for both, and it becomes a drawer
 * with a scrim, which is the honest thing to do at that width.
 *
 * Width is dragged from the left edge and remembered per `storageKey`, so a
 * reviewer who wants a wide panel gets it back tomorrow.
 */

const MIN_W = 360;
const MAX_W = 820;
const OVERLAY_BELOW = 1100;

function readWidth(key, fallback) {
  try {
    const raw = Number(localStorage.getItem(key));
    if (Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W) return raw;
  } catch (_) { /* private mode, or storage blocked */ }
  return fallback;
}

export default function SidePanel({
  open,
  onClose,
  title,
  actions = null,
  storageKey = 'trodo.sidePanel.width',
  defaultWidth = 480,
  ariaLabel,
  // False while the page has something of its own above the panel: Escape
  // belongs to the topmost layer, not to whatever was opened first.
  closeOnEscape = true,
  children,
}) {
  // undefined until we have looked: the slot is rendered by an ancestor, so
  // it cannot be read during the first render. Looking is one frame.
  const [slot, setSlot] = useState(undefined);
  const [width, setWidth] = useState(() => readWidth(storageKey, defaultWidth));
  const [overlay, setOverlay] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < OVERLAY_BELOW : false));
  const widthRef = useRef(width);
  widthRef.current = width;
  const closeRef = useRef(null);

  // Opening moves focus into the panel, so a keyboard reaches it without
  // tabbing back through the page it was opened from. `slot` is in the deps
  // because nothing is rendered until it resolves, one frame after mount.
  useEffect(() => { if (open) closeRef.current?.focus(); }, [open, slot]);

  useEffect(() => { setSlot(document.getElementById(SIDE_PANEL_SLOT_ID) || null); }, []);
  useEffect(() => {
    const onResize = () => setOverlay(window.innerWidth < OVERLAY_BELOW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (!open || !closeOnEscape) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, onClose]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => setWidth(Math.min(MAX_W, Math.max(MIN_W, startW + (startX - ev.clientX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(storageKey, String(widthRef.current)); } catch (_) { /* private mode */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [storageKey]);

  if (!open) return null;

  const panel = (
    <aside
      className={`relative flex min-h-0 w-full flex-1 flex-col overflow-hidden border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)] ${overlay ? '' : 'rounded-tr-2xl border-t'}`}
      aria-label={ariaLabel || (typeof title === 'string' ? title : 'Panel')}
    >
      {!overlay && (
        <div onMouseDown={startResize} title="Drag to resize"
          className="absolute bottom-0 left-0 top-0 z-10 -ml-0.5 w-1 cursor-col-resize hover:bg-[color:var(--trodo-primary)]/30" />
      )}
      <div className="flex shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-3 py-2">
        <div className="min-w-0 flex-1 truncate text-[13px] font-medium text-[color:var(--text-primary)]">{title}</div>
        {actions}
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" title="Close"
          className="rounded-md p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
          <X size={15} strokeWidth={2} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
    </aside>
  );

  const body = (
    <div className={`flex shrink-0 flex-col overflow-hidden bg-[color:var(--surface-sunken)] ${overlay ? '' : 'pt-2'}`}
      style={{ width: overlay ? 'min(100vw, 460px)' : width }}>
      {panel}
    </div>
  );

  if (slot === undefined) return null;
  // Narrow, or rendered somewhere with no slot beside the content column
  // (a page outside the app shell): a drawer is the honest thing at that
  // point, rather than a panel wedged into the middle of the page.
  if (overlay || !slot) {
    return (
      <>
        <div className="fixed inset-0 z-[60] bg-black/15" onClick={onClose} aria-hidden />
        <div className="fixed bottom-0 right-0 top-0 z-[61] flex">{body}</div>
      </>
    );
  }
  // Beside the content column, in the app's own row — not inside the page,
  // which is why the page shrinks instead of being covered.
  return createPortal(body, slot);
}
